import React from 'react';

export interface GlassCardProps {
  variant?: 'default' | 'strong' | 'hover';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

export const GlassCard: React.FC<GlassCardProps> = ({
  variant = 'default',
  padding = 'md',
  children,
  style = {},
  className = '',
  onClick,
  onMouseDown,
}) => {
  const getVariantStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      borderRadius: 'var(--radius-lg)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid var(--stroke)',
      transition: 'all var(--transition-normal)',
    };

    switch (variant) {
      case 'strong':
        return {
          ...baseStyles,
          background: 'var(--panel-strong)',
        };
      case 'hover':
        return {
          ...baseStyles,
          background: 'var(--panel)',
          cursor: onClick ? 'pointer' : 'default',
        };
      default:
        return {
          ...baseStyles,
          background: 'var(--panel)',
        };
    }
  };

  const getPaddingStyles = (): React.CSSProperties => {
    switch (padding) {
      case 'none':
        return { padding: 0 };
      case 'sm':
        return { padding: 'var(--space-2)' };
      case 'lg':
        return { padding: 'var(--space-6)' };
      case 'md':
      default:
        return { padding: 'var(--space-4)' };
    }
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (variant === 'hover') {
      e.currentTarget.style.background = 'var(--panel-hover)';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (variant === 'hover') {
      e.currentTarget.style.background = 'var(--panel)';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }
  };

  return (
    <div
      className={`glass-card ${className}`}
      style={{
        ...style,
        ...getVariantStyles(),
        ...getPaddingStyles(),
      }}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

export default GlassCard;
